import { useState } from "react";
import { createPortal } from "react-dom";
import api, { euro, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Trash2, X } from "lucide-react";

export const delLabel = (descr, importo, data) => [descr || "Voce", importo != null && importo !== "" ? euro(importo) : null, data ? fmtDate(data) : null].filter(Boolean).join(" – ");

// Selezione multipla per le liste di Finanza (solo Soci).
export function useFinSelection() {
  const [sel, setSel] = useState({});
  const toggle = (key, item) => setSel((s) => { const n = { ...s }; if (n[key]) delete n[key]; else n[key] = item; return n; });
  return { sel, toggle, clear: () => setSel({}), count: Object.keys(sel).length, has: (k) => !!sel[k] };
}

export function SelBox({ selection, tipo, id, label, modo, mese }) {
  const { isDirezione } = useAuth();
  if (!isDirezione || !selection) return null;
  const k = `${tipo}:${id}`;
  return <input type="checkbox" data-testid={`sel-${tipo}-${id}`} checked={selection.has(k)} onChange={() => selection.toggle(k, { tipo, id, label, modo, mese })}
    onClick={(e) => e.stopPropagation()} className="w-4 h-4 accent-[#FC82ED] shrink-0 cursor-pointer" aria-label="Seleziona" />;
}

async function eseguiElimina(items) {
  const r = await api.post("/finanza/elimina", { items });
  if (r.data.errori?.length) toast.error(`${r.data.errori.length} voci non eliminate: ${r.data.errori[0].errore}`);
  if (r.data.eliminati) toast.success(r.data.eliminati === 1 ? "Voce eliminata (nel Cestino per 30 giorni)" : `${r.data.eliminati} voci eliminate (nel Cestino per 30 giorni)`);
}

function ConfirmDelete({ title, label, options, onClose, onConfirm }) {
  const [modo, setModo] = useState(options?.[0]?.[0]);
  const [busy, setBusy] = useState(false);
  return createPortal(
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-4" data-testid="fin-delete-confirm">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md card-immo p-5">
        <div className="flex items-center justify-between mb-2"><h3 className="font-display font-bold text-lg">{title || "Elimina voce"}</h3><button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100"><X size={18} /></button></div>
        <p className="text-sm mb-3" data-testid="fin-delete-text">{label}</p>
        {options && (
          <div className="space-y-1.5 mb-3">
            {options.map(([v, l]) => (
              <label key={v} data-testid={`fin-delete-opt-${v}`} className={`flex items-start gap-2 p-2.5 rounded-xl border text-sm cursor-pointer ${modo === v ? "border-red-300 bg-red-50" : "border-[var(--border)]"}`}>
                <input type="radio" checked={modo === v} onChange={() => setModo(v)} className="mt-0.5" /> {l}
              </label>
            ))}
          </div>
        )}
        <p className="text-[11px] text-[var(--muted)] mb-4">La voce va nel Cestino: puoi ripristinarla entro 30 giorni.</p>
        <div className="flex gap-2">
          <button data-testid="fin-delete-cancel" onClick={onClose} className="flex-1 py-2.5 rounded-xl bg-[#F7F7F8] border border-[var(--border)] text-sm font-medium">Annulla</button>
          <button data-testid="fin-delete-ok" disabled={busy} onClick={async () => { setBusy(true); try { await onConfirm(modo); } finally { setBusy(false); } }}
            className="flex-1 py-2.5 rounded-xl bg-red-600 text-white text-sm font-semibold disabled:opacity-50">{busy ? "Elimino…" : "Elimina"}</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// options: [[modo, testo]] per le spese fisse (es. solo questo mese / da questo mese in poi).
export function DeleteBtn({ tipo, id, label, options, mese, onDone }) {
  const { isDirezione } = useAuth();
  const [open, setOpen] = useState(false);
  if (!isDirezione) return null;
  const confirm = async (modo) => {
    try { await eseguiElimina([{ tipo, id, modo, mese }]); setOpen(false); onDone && onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore nell'eliminazione"); }
  };
  return (
    <>
      <button data-testid={`del-${tipo}-${id}`} title="Elimina" onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className="p-1.5 rounded-lg border border-[var(--border)] text-slate-500 hover:text-red-600 hover:border-red-200 shrink-0"><Trash2 size={14} /></button>
      {open && <ConfirmDelete label={`Eliminare '${label}'?`} options={options} onClose={() => setOpen(false)} onConfirm={confirm} />}
    </>
  );
}

export function BulkDeleteBar({ selection, onDone }) {
  const { isDirezione } = useAuth();
  const [open, setOpen] = useState(false);
  if (!isDirezione || !selection || selection.count === 0) return null;
  const items = Object.values(selection.sel);
  const confirm = async () => {
    try { await eseguiElimina(items.map(({ tipo, id, modo, mese }) => ({ tipo, id, modo, mese }))); selection.clear(); setOpen(false); onDone && onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore nell'eliminazione"); }
  };
  return (
    <div className="sticky top-16 z-30 flex items-center justify-between gap-3 p-3 rounded-xl border border-red-200 bg-red-50" data-testid="fin-bulk-bar">
      <span className="text-sm"><b>{selection.count}</b> {selection.count === 1 ? "voce selezionata" : "voci selezionate"}</span>
      <div className="flex gap-2">
        <button data-testid="fin-bulk-clear" onClick={selection.clear} className="px-3 py-1.5 rounded-lg bg-white border border-[var(--border)] text-xs font-medium">Deseleziona</button>
        <button data-testid="fin-bulk-delete" onClick={() => setOpen(true)} className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-red-600 text-white text-xs font-semibold"><Trash2 size={13} /> Elimina selezionati</button>
      </div>
      {open && <ConfirmDelete title="Elimina selezionati" label={`Eliminare ${items.length} voci? ${items.slice(0, 3).map((i) => `'${i.label}'`).join(", ")}${items.length > 3 ? "…" : ""}`} onClose={() => setOpen(false)} onConfirm={confirm} />}
    </div>
  );
}
